import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AppStoreBadge from '../components/AppStoreBadge';
import GooglePlayBadge from '../components/GooglePlayBadge';
import { isNativeApp } from '../utils/isNativeApp';
import { resolvePartnerInvite, clearPartnerInvite } from '../utils/partnerInvite';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', confirm_password: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  // Co-borrower invite params from URL
  const isCoborrower = searchParams.get('coborrower') === 'true';
  const inviteToken = searchParams.get('token') || '';
  // Partner referral invite
  // ⚠️ NOT `searchParams.get('invite')` ALONE — THAT IS THE BUG. The token used to
  // live only in the query string, so a buyer who followed the invite link and
  // came back later (bookmark, app, retyped address) registered with no token and
  // the referral stayed 'pending' forever, silently. See utils/partnerInvite.js.
  // ⚠️ useMemo, NOT A BARE CALL. resolvePartnerInvite WRITES (it promotes a URL
  // token into storage), and a write on every render is a side effect in render —
  // it would fire twice per render under StrictMode and again on every keystroke
  // in the form. Resolved once per URL, which is the only time the answer changes.
  const urlInvite = searchParams.get('invite') || '';
  const partnerInviteToken = useMemo(() => resolvePartnerInvite(urlInvite), [urlInvite]);

  // ── 🚨 PREFILL FROM A PARTNER INVITE, AND THE URL CLEANED AFTERWARDS ─────
  //
  // The borrower's partner already gave Doorly their name and email; retyping
  // them is friction that also invites a typo, and a typo in the email is how a
  // submitted file fails to link to the account that was just created.
  //
  // 🔑 THE TOKEN LEAVES THE ADDRESS BAR ONCE READ. history.replaceState keeps it
  // out of the back-button history, out of the Referer header on any subsequent
  // navigation, and out of anything reading location.search. resolvePartnerInvite
  // above has already promoted it into storage, so clearing the URL loses
  // nothing — the POST still carries it.
  //
  // ⚠️ GOOGLE ADS IS NOT A CONCERN HERE AND THAT WAS CHECKED, not assumed:
  // /register is not in MARKETING_ROUTES (utils/marketingTag.js), so the tag
  // does not load on this route at all — and the identifier gate would deny it
  // even if it were listed, because the query carries a long opaque value.
  // SENTRY WAS a concern: event.request.url and history breadcrumbs are strings
  // that deepRedact never reached. See scrubUrl in utils/sentryScrub.js.
  const [prefilledEmail, setPrefilledEmail] = useState('');
  const [prefillCleared, setPrefillCleared] = useState(false);

  useEffect(() => {
    if (!urlInvite) return;
    let alive = true;
    fetch(`${API_URL}/api/auth/invite/${encodeURIComponent(urlInvite)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d || !d.invite) return;
        // 🔑 MERGED, NOT ASSIGNED. A borrower who started typing before the
        // round trip finished must not have their input overwritten.
        setForm((f) => ({
          ...f,
          first_name: f.first_name || d.invite.firstName || '',
          last_name: f.last_name || d.invite.lastName || '',
          email: f.email || d.invite.email || '',
        }));
        if (d.invite.email) setPrefilledEmail(d.invite.email);
      })
      // ⚠️ A FAILED RESOLVE IS A BLANK FORM, NOT AN ERROR. Expired, used and
      // unknown tokens all answer { invite: null }; the page says nothing about
      // which, because the borrower's next action is the same either way.
      .catch(() => {});
    return () => { alive = false; };
  }, [urlInvite]);

  // 🚨 SEPARATE FROM THE FETCH, so the URL is cleaned whether or not the token
  // resolved. A token that turned out to be expired is still a token that
  // should not sit in history.
  useEffect(() => {
    if (!urlInvite || typeof window === 'undefined' || !window.history?.replaceState) return;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('invite');
      window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
    } catch (_) { /* a URL we cannot parse is one we cannot clean */ }
  }, [urlInvite]);

  const [inviteData, setInviteData] = useState(null); // { coborrower_first_name, primary_first_name, ... }
  const [inviteError, setInviteError] = useState(''); // 'expired' | 'invalid' | ''
  const [inviteLoading, setInviteLoading] = useState(isCoborrower && !!inviteToken);

  const prefill = location.state?.prefill || {};

  // Validate invite token on mount if this is a co-borrower registration
  useEffect(() => {
    if (!isCoborrower || !inviteToken) { setInviteLoading(false); return; }
    axios.get(`${API_URL}/api/coborrower/validate-token/${inviteToken}`)
      .then((res) => {
        setInviteData(res.data);
        setForm((prev) => ({
          ...prev,
          first_name: res.data.coborrower_first_name || '',
          last_name: res.data.coborrower_last_name || '',
          email: res.data.coborrower_email || '',
        }));
      })
      .catch((err) => {
        const errData = err.response?.data;
        if (errData?.error === 'expired') {
          setInviteError('expired');
        } else {
          setInviteError('invalid');
        }
      })
      .finally(() => setInviteLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    setServerError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.last_name.trim())  errs.last_name  = 'Last name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'A valid email is required.';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    setServerError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        buying_state: prefill.buyingState || '',
        buying_stage: prefill.buyerStage || '',
        // Pre-qual answers so they persist even if the borrower never
        // finishes the application (the pipeline reads these).
        purchase_price: prefill.purchasePrice || '',
        credit_score: prefill.creditScore || '',
        partner_invite_token: partnerInviteToken || undefined,
        // 🔑 CONSUMED SERVER-SIDE: marks the invite single-use and lets the
        // backend resolve partner attribution from it. See routes/auth.js.
        invite_token: partnerInviteToken || undefined,
        // 🚨 THE CO-BORROWER LINK IS NOW THE SERVER'S JOB, not a follow-up call
        // from this page. It used to be POST /coborrower/accept below, inside a
        // `catch (_) {}` — so a failure left a registered user whose loan file
        // still read "Invited", with nothing recorded anywhere. routes/auth.js
        // links the connection in the same request that creates the account.
        coborrower_token: (isCoborrower && inviteToken) ? inviteToken : undefined,
      });
      // ⚠️ CLEARED ON ANY SUCCESSFUL REGISTRATION, whether or not the token was
      // used. Once this browser has made an account, a token still sitting in
      // storage can only ever attach the wrong person to somebody else's partner.
      clearPartnerInvite();
      login(res.data.token, res.data.user);

      // ── the fallback link, and it is no longer silent ────────────────────
      //
      // ⚠️ KEPT AS A SECOND CHANCE, NOT AS THE MECHANISM. Registration already
      // linked the connection server-side; this covers a session that reached
      // this page without the token in the register payload. It answers 400
      // 'Already accepted' in the normal case, which is success, not an error.
      //
      // 🚨 EVERY OTHER FAILURE IS LOGGED. `catch (_) {}` is what let "Mario
      // registered but Cathy's file still says Invited" happen with no trace on
      // either side. It must not block the redirect — the account exists and the
      // borrower should get on with it — but it must leave something behind.
      if (isCoborrower && inviteToken) {
        try {
          await axios.post(`${API_URL}/api/coborrower/accept/${inviteToken}`, {}, {
            headers: { Authorization: `Bearer ${res.data.token}` },
          });
        } catch (e) {
          const already = e.response?.status === 400
            && /already accepted/i.test(e.response?.data?.error || '');
          if (!already) {
            console.error('[Register] co-borrower link fallback failed:',
              e.response?.status, e.response?.data?.error || e.message);
          }
        }
      }

      // ── 🚨 A WAITING SUBMISSION IS NOT A BLANK APPLICATION ─────────────
      //
      // This navigated to /apply?step=1 unconditionally. A borrower who
      // registered from a broker-submission invite — their originator typed the
      // whole application and sent it — landed in the empty direct-to-consumer
      // wizard with none of it, which is exactly what Hannah Testy reported:
      // "it looks like a brand new, normal application flow".
      //
      // ── 🚨 EVERY BORROWER GOES TO STEP 1, INCLUDING THIS ONE ──────────────
      //
      // This branch asked GET /api/borrower/submission and sent anyone with a
      // waiting file to /broker-application instead — a screen where they
      // confirmed their broker's answers field by field, and then arrived here
      // anyway to enter the same values again. Jesse: "land LO-invited
      // borrowers on step 1 of the application with all of their info filled
      // out […] That would remove the duplicate work for the borrower… it
      // would also more closely align the borrower journey with all other
      // borrowers from different sources."
      //
      // 🔑 SO THERE IS NO BRANCH LEFT TO MAKE, and the lookup that drove it is
      // gone with it. What makes their step 1 different is the form: the
      // submission was linked to this account during registration, so
      // brokerPrefill fills every field their broker answered and badges each
      // one "From {broker} — edit if it's wrong".
      navigate('/apply?step=1', {
        state: {
          prefill: {
            purchasePrice:    prefill.purchasePrice   || '',
            downPaymentRange: prefill.downPaymentRange || '',
            creditScore:      prefill.creditScore     || '',
            buyingState:      prefill.buyingState     || '',
          },
        },
      });
    } catch (err) {
      setServerError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name) =>
    `w-full h-14 px-4 rounded-xl border text-base text-black placeholder-gray-400 bg-white outline-none transition-all focus:ring-2 focus:ring-[#3a6ad9]/30 focus:border-[#3a6ad9] ${
      errors[name] ? 'border-red-400' : 'border-gray-200 hover:border-gray-300'
    }`;

  // Loading state while validating invite token
  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin w-8 h-8 mx-auto mb-3 text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-500 text-sm">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired token error page
  if (isCoborrower && inviteError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center">
              <img src={`${process.env.PUBLIC_URL}/doorly_logo_black.png`} alt="Doorly" style={{ height: 40, width: 'auto', display: 'block' }} />
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-black mb-2">
              {inviteError === 'expired' ? 'Invitation Expired' : 'Invalid Invitation Link'}
            </h1>
            <p className="text-gray-500 text-sm mb-6">
              {inviteError === 'expired'
                ? 'This invitation link has expired or is no longer valid. Please ask your co-borrower to resend the invitation.'
                : 'This invitation link is invalid or has already been used.'}
            </p>
            <Link to="/" className="btn-primary text-white hover:opacity-90" style={{ backgroundColor: '#3a6ad9' }}>
              Go to Doorly Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <img src={`${process.env.PUBLIC_URL}/doorly_logo_black.png`} alt="Doorly" style={{ height: 40, width: 'auto', display: 'block' }} />
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">Create your account</h1>
          <p className="text-gray-500 text-sm">Save your progress and pick up where you left off.</p>
        </div>

        {/* Partner referral invite banner */}
        {partnerInviteToken && (
          <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: '#f0fdf4', border: '1px solid #acf44e', color: '#166534' }}>
            🎉 <strong>You've been referred to Doorly!</strong> Create your account to get started — your application will be linked to your referring partner.
          </div>
        )}

        {/* Co-borrower invite banner */}
        {isCoborrower && inviteData && (
          <div className="mb-4 px-4 py-3 rounded-xl text-white text-sm" style={{ backgroundColor: '#3a6ad9' }}>
            🏠 You have been invited to co-borrow with <strong>{inviteData.primary_first_name}</strong>. Create your account to get started.
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8">
          {serverError && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                <input name="first_name" type="text" className={inputClass('first_name')} placeholder="Jane" value={form.first_name} onChange={handleChange} />
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                <input name="last_name" type="text" className={inputClass('last_name')} placeholder="Smith" value={form.last_name} onChange={handleChange} />
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              {/* 🚨 READ-ONLY WHEN PREFILLED, because this address is what links
                  the file your partner sent to the account you are creating. A
                  borrower who edits it here gets an account with no file
                  attached and nothing on screen explaining why. */}
              <input name="email" type="email"
                className={inputClass('email')}
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                readOnly={!!prefilledEmail && !prefillCleared} />
              {!!prefilledEmail && !prefillCleared && (
                <>
                  <p className="text-gray-500 text-xs mt-1">
                    This is the address your application was sent to.
                  </p>
                  {/* 🔑 AN ESCAPE HATCH THAT STATES THE COST. Somebody really may
                      have been sent an invite at the wrong address, or not be the
                      person named — and a read-only field with no way out is a
                      dead end. What it must not be is a silent one: clearing the
                      prefill means the file does not follow. */}
                  <button type="button"
                    onClick={() => {
                      setPrefillCleared(true);
                      setForm((f) => ({ ...f, first_name: '', last_name: '', email: '' }));
                    }}
                    className="text-xs text-blue-600 hover:underline mt-1">
                    Not you, or wrong email?
                  </button>
                </>
              )}
              {prefillCleared && (
                <p className="text-amber-700 text-xs mt-1">
                  You can use any address. The application sent for you was
                  addressed to {prefilledEmail}, and it will not link to a different one.
                </p>
              )}
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input name="password" type="password" className={inputClass('password')} placeholder="Min. 8 characters" value={form.password} onChange={handleChange} />
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
              <input name="confirm_password" type="password" className={inputClass('confirm_password')} placeholder="Re-enter your password" value={form.confirm_password} onChange={handleChange} />
              {errors.confirm_password && <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-full text-white gap-2 hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating Account...
                </>
              ) : (
                <>
                  <span>Create Account & Apply</span>
                  <span className="btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                  </span>
                </>
              )}
            </button>

            {/* ⚠️ ACCEPTANCE IS IMPLIED HERE, so the documents have to be
                reachable here. Nothing records that a user accepted them — there
                is no terms_accepted_at column and no version — so this line is
                the only place the agreement is expressed at all. */}
            <p className="text-center text-xs text-gray-500 mt-4 leading-relaxed">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="underline" style={{ color: '#3a6ad9' }}>Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline" style={{ color: '#3a6ad9' }}>Privacy Policy</Link>.
            </p>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Log in
          </Link>
        </p>

        {/* Download badges — web app only; hidden inside the native app */}
        {!isNativeApp() && (
        <div className="flex flex-col items-center" style={{ marginTop: 16 }}>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Manage your application on the go</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <AppStoreBadge />
            <GooglePlayBadge />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default Register;
